import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Sprout, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWorld } from "@/context/WorldContext";
import { useOrigin } from "@/components/Origin";
import { CARD_H, CARD_W, ends } from "@/lib/family";
import { layoutLineage } from "@/lib/lineageLayout";
import { buildLineage, focusLineage, lineageTitle } from "@/lib/lineage";
import { PanZoom } from "@/components/PanZoom";
import { LINE_STYLES, SpeciesLines } from "@/components/SpeciesLines";
import { SpeciesNode } from "@/components/SpeciesNode";

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-code text-slate-400" data-testid="tol-legend">
    {Object.entries(LINE_STYLES).map(([k, { label, style }]) => (
      <span key={k} className="flex items-center gap-2">
        <svg width="28" height="8"><line x1="0" y1="4" x2="28" y2="4" {...style} /></svg>{label}
      </span>
    ))}
  </div>
);

const Empty = ({ title, body, create }) => {
  const { basePath, readOnly } = useWorld();
  return (
    <div className="rounded-2xl border border-dashed border-sky-500/30 p-14 text-center" data-testid="tol-empty">
      <Sprout className="w-8 h-8 text-sky-400/70 mx-auto mb-3" />
      <p className="font-display text-3xl text-sky-50">{title}</p>
      <p className="text-slate-500 mt-2">{body}</p>
      {create && !readOnly && (
        <Link to={`${basePath}/new?category=Species`} className="inline-flex items-center gap-1.5 mt-5 rounded-full bg-sky-400 hover:bg-sky-300 text-onaccent text-sm font-semibold px-5 py-2.5 transition-colors" data-testid="tol-create-species-btn">
          <Plus className="w-4 h-4" /> Create a species
        </Link>
      )}
    </div>
  );
};

const FocusPicker = ({ tree, focus, setFocus }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Select value={focus || "all"} onValueChange={(v) => setFocus(v === "all" ? "" : v)}>
      <SelectTrigger className="w-60" data-testid="tol-focus-select"><SelectValue placeholder="Focus on a species" /></SelectTrigger>
      <SelectContent className="bg-panel">
        <SelectItem value="all">Focus on a species…</SelectItem>
        {tree.ids.map((id) => tree.byId.get(id)).sort((a, b) => a.title.localeCompare(b.title)).map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`tol-focus-option-${s.id}`}>{s.title}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {focus && (
      <button onClick={() => setFocus("")} className="flex items-center gap-1.5 text-sm rounded-full border border-line px-4 py-2 text-slate-300 hover:border-sky-500/40 hover:text-sky-200 transition-colors" data-testid="tol-show-all-btn">
        <X className="w-3.5 h-3.5" /> Show all species
      </button>
    )}
  </div>
);

const Loners = ({ loners }) => (
  <div className="rounded-xl border border-line bg-panel p-4" data-testid="tol-loners">
    <p className="text-sm text-slate-300 mb-3">Unconnected species <span className="text-slate-500">({loners.length})</span></p>
    <div className="flex flex-wrap gap-3">
      {loners.map((s, i) => (
        <div key={s.id} className="relative" style={{ width: CARD_W, height: CARD_H }}>
          <SpeciesNode page={s} p={{ x: 0, y: 0 }} i={i} onHover={() => {}} />
        </div>
      ))}
    </div>
  </div>
);

export default function TreeOfLife() {
  const { pages, toggle } = useOrigin();
  const [hi, setHi] = useState(null);
  const [params, setParams] = useSearchParams();
  const tree = useMemo(() => buildLineage(pages), [pages]);
  const focus = tree.ids.includes(params.get("focus")) ? params.get("focus") : "";
  const setFocus = (id) => setParams(id ? { focus: id } : {}, { replace: true });
  const view = useMemo(() => (focus ? focusLineage(tree, focus) : tree), [tree, focus]);
  const layout = useMemo(() => layoutLineage(view.ids, view.edges, tree.byId,
    focus ? () => `Focus: ${tree.byId.get(focus).title}` : lineageTitle(tree)), [view, tree, focus]);
  const boxes = layout.groups.map((g) => { const w = Math.max(g.w, CARD_W * 2) + 48; return { ...g, left: (g.w - w) / 2, w }; });
  const minX = Math.min(0, ...boxes.map((b) => b.left)), maxX = Math.max(layout.width, ...boxes.map((b) => b.left + b.w));
  const near = useMemo(() => {
    if (!hi) return null;
    const s = new Set([hi]);
    view.edges.forEach((e) => { const [a, b] = ends(e); if (a === hi) s.add(b); if (b === hi) s.add(a); });
    return s;
  }, [hi, view]);

  return (
    <div className="px-4 md:px-12 py-10 max-w-7xl space-y-5" data-testid="tree-of-life-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-code text-xs uppercase tracking-[0.3em] text-sky-400/80 mb-2">Bestiary · Lineages</p>
          <h1 className="font-display text-4xl sm:text-5xl text-sky-50">Tree of life</h1>
          <p className="text-sm text-slate-500 mt-2">Ancestors sit above their descendants and subspecies. Hybrids sit between their parent species.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">{toggle}{tree.ids.length > 0 && <FocusPicker tree={tree} focus={focus} setFocus={setFocus} />}</div>
      </div>
      {tree.empty ? <Empty title="No species yet." body="Create your first species to start growing the tree." create /> : (
        <>
          {tree.ids.length === 0 ? (
            <Empty title="No lineages yet." body="Link species under Species Information → Related Species to see how they branch." />
          ) : (
            <>
              <Legend />
              <PanZoom width={maxX - minX} height={layout.height + 32} fitKey={`${focus}-${view.ids.length}`} testPrefix="tol">
                <div className="absolute" style={{ left: -minX, top: 8 }}>
                {boxes.map((g, i) => (
                  <div key={i} className="absolute rounded-2xl border border-sky-500/15 bg-panel/30" style={{ left: g.left, top: g.y - 8, width: g.w, height: g.h + 32 }} data-testid={`tol-group-${i}`}>
                    <h2 className="font-display text-2xl text-sky-200/90 px-6 pt-3">{g.title}</h2>
                  </div>
                ))}
                <svg width={layout.width} height={layout.height} className="absolute inset-0 overflow-visible pointer-events-none"><SpeciesLines edges={view.edges} pos={layout.pos} hi={hi} /></svg>
                {[...layout.pos.entries()].map(([id, p], i) => (
                  <SpeciesNode key={id} i={i} page={tree.byId.get(id)} p={p} dim={near && !near.has(id)} onHover={setHi} focused={id === focus} pannable />
                ))}
                </div>
              </PanZoom>
            </>
          )}
          {!focus && tree.loners.length > 0 && <Loners loners={tree.loners} />}
        </>
      )}
    </div>
  );
}
